import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { AboutProject } from "../../components/Onboarding/AboutProject";
import { EmailOnboard } from "../../components/Onboarding/EmailOnboard";
import { Login } from "../../components/Onboarding/Login";
import { SignUp } from "../../components/Onboarding/SignUp";
import { OnboardingContext } from "../../context";
import AuthService from "../../service/authService";
import { COUNTRY, Country, Currency, UserOnboardingInfo } from "../../types";
import { ONBOARDING_STEPS, ROUTES } from "../../constant/common.constant";
import { getLanguage } from "../../utils/LocaleUtil";
import { useTokenInfo } from "../../utils/ReactHooks";
import { useUserInfoFetcher } from "../../utils/UserHooks";
import { OnboardingTemplate } from "./OnboardingTemplate";
import {
  identifyUser,
  trackAccountCreated,
  trackSignIn,
} from "../../utils/analytics.utils";
import { useTeam } from "../../context/Team";

export const OnboardingContainer = () => {
  const { token: inviteToken } = useParams();
  //Change the language if the URL param set a specific language
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();

  //Query parameter campaign (use for analytics)
  const campaign = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get("campaign");
  }, [location.search]);

  const [currentStep, setCurrentStep] = useState<ONBOARDING_STEPS>(
    ONBOARDING_STEPS.EMAIL
  );

  const [isAuthorizedDistributor, setIsAuthorizedDistributor] = useState(false);
  const { refetchTeam } = useTeam();
  const { initUserInfo } = useUserInfoFetcher();

  const [onboardingInfo, setOnboardingInfo] = useState<UserOnboardingInfo>({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    optNewsletter: true,
    language: "",
    country: COUNTRY.CA,
    forWho: undefined,
    contactPhone: "",
    projectSize: "",
    teamName: "",
    eventDate: undefined,
    campaign: "",
  });

  //Campaign url query parameter may not always be set
  useEffect(() => {
    setOnboardingInfo((infos) => ({
      ...infos,
      campaign: campaign || "",
    }));
  }, [campaign, setOnboardingInfo]);

  useEffect(() => {
    setOnboardingInfo((infos) => ({
      ...infos,
      language: getLanguage(),
    }));
  }, [setOnboardingInfo]);

  const [errorMsg, setErrorMsg] = useState("");
  const tokenInfo = useTokenInfo(inviteToken);
  /**
   *
   */
  const onCheckEmailNextStep = useCallback(
    async (email: string) => {
      //Check if user exits, if exists send to login step, if doesn't exists, send to signup step
      setErrorMsg("");
      setIsAuthorizedDistributor(false);
      setOnboardingInfo((prev) => ({ ...prev, email }));
      try {
        await AuthService.userExists(email);
        setCurrentStep(ONBOARDING_STEPS.LOGIN);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        const statusCode = error?.response?.status;

        if (errorMessage === "AUTHORIZED_DISTRIBUTOR") {
          setIsAuthorizedDistributor(true);
        } else if (statusCode === 429) {
          setErrorMsg("onboard.too_many_requests");
          return;
        }

        setCurrentStep(ONBOARDING_STEPS.SIGNUP);
      }
    },
    [setCurrentStep, t]
  );

  /**
   *
   */
  const onBackToEmailStep = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ONBOARDING_STEPS.EMAIL);
      setIsAuthorizedDistributor(false);
    },
    [setCurrentStep]
  );

  /**
   *
   */
  const onBackToUserInfoStep = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      setCurrentStep(ONBOARDING_STEPS.SIGNUP);
    },
    [setCurrentStep]
  );

  /**
   *
   * @param {*} e
   * @param {*} email
   * @param {*} password
   * @returns
   */
  const onSignInClick = async (email: string, password: string) => {
    try {
      const res = await AuthService.login({
        email,
        password,
        inviteToken: _.isEmpty(inviteToken) ? null : inviteToken,
      }).catch((error) => {
        const statusCode = error?.response?.status;
        const errorMessage =
          statusCode === 429
            ? "too_many_requests"
            : (error?.response?.data?.message ?? "error");
        setErrorMsg("sign_in." + errorMessage);
        return { data: error.response.data };
      });

      const user = res.data.data.user as {
        country: Country | null;
        currency: Currency;
        email: string;
        id: string;
      };

      if (res?.data?.status === "success") {
        AuthService.setLocalUser(user.currency, user.country ?? COUNTRY.CA);

        const userInfo = await AuthService.getUserInfo();

        initUserInfo();

        trackSignIn();

        identifyUser(user.id.toString(), {
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          firstName: userInfo?.firstName ?? "",
          lastName: userInfo?.lastName ?? "",
          email: userInfo?.email ?? "",
          role: userInfo?.roles[0].role ?? "",
          forWho: userInfo?.forWho ?? "",
        });

        //if no invite token, then route to the user's projects
        if (!inviteToken) {
          navigate(ROUTES.HOME);
          return;
        }

        const teamId = res.data.data.joinedTeam.id;

        refetchTeam();

        navigate(generatePath(ROUTES.TEAM, { teamId }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Last step
   */
  const onSignUpGetStarted = useCallback(
    async (values?: UserOnboardingInfo) => {
      setErrorMsg("");
      const formValues = values ?? onboardingInfo;
      //Dates can't be an empty string, so we must set eventDate to null if it is ""
      formValues.eventDate = undefined;

      formValues.inviteToken = inviteToken || undefined;

      formValues.language = getLanguage();
      const res = await AuthService.signUp(formValues).catch((error) => {
        return error.response;
      });

      const { status, data, message } = res.data;

      if (status !== "success") {
        if (message === "username_already_exists") {
          setErrorMsg(t("sign_up." + message, { email: formValues.email }));
          return;
        }
        setErrorMsg(t("sign_up." + message));
        return;
      }

      const { newUser, newProject, joinedTeam, newTeam } = data;

      //Equivalent of login right after signup
      AuthService.setLocalUser(newUser.currency, newUser.country);

      initUserInfo();

      //Analytics
      if (_.isEmpty(formValues.projectSize)) {
        trackAccountCreated({
          firstName: formValues.firstName ?? "",
          lastName: formValues.lastName ?? "",
          email: formValues.email ?? "",
          invited: inviteToken ? true : false, // whether the user signed up via an invitation link
          forWho: formValues.forWho,
          currency: newUser.currency,
          country: formValues.country ?? COUNTRY.CA,
          contactPhone: formValues.contactPhone,
        });
      } else {
        trackAccountCreated({
          firstName: formValues.firstName ?? "",
          lastName: formValues.lastName ?? "",
          email: formValues.email ?? "",
          invited: inviteToken ? true : false, // whether the user signed up via an invitation link
          projectSize: formValues.projectSize,
          label: `${formValues.forWho} / ${formValues.projectSize}`,
          campaign: formValues.campaign,
          currency: newUser.currency,
          forWho: formValues.forWho,
          country: formValues.country ?? COUNTRY.CA,
          contactPhone: formValues.contactPhone,
        });
      }

      ///if no invite token, then route to the newly created project and popup the create design
      if (!inviteToken) {
        navigate(
          generatePath(ROUTES.PROJECT, {
            projectId: newProject.id,
            teamId: newTeam.id,
          })
        );
        return;
      }

      //Push to joined team's page
      navigate(generatePath(ROUTES.TEAM, { teamId: joinedTeam.id }));
    },
    [setErrorMsg, onboardingInfo, initUserInfo, t, navigate, inviteToken]
  );

  /**
   *
   */
  const onCheckUserInfoNextStep = useCallback(
    async (values: UserOnboardingInfo) => {
      setOnboardingInfo(values);
      setCurrentStep(ONBOARDING_STEPS.ABOUT_PROJECT);
    },
    [setCurrentStep]
  );

  return (
    <OnboardingContext.Provider value={{ errorMsg, tokenInfo }}>
      <OnboardingTemplate
        hideMarquee={currentStep === ONBOARDING_STEPS.ABOUT_PROJECT}
      >
        {currentStep === ONBOARDING_STEPS.EMAIL && (
          <EmailOnboard
            onNextStep={onCheckEmailNextStep}
            email={onboardingInfo.email}
          />
        )}
        {currentStep === ONBOARDING_STEPS.LOGIN && !!onboardingInfo.email && (
          <Login
            onNextStep={onSignInClick}
            onPreviousStep={onBackToEmailStep}
            email={onboardingInfo.email}
          />
        )}
        {currentStep === ONBOARDING_STEPS.SIGNUP && (
          <SignUp
            onNextStep={onCheckUserInfoNextStep}
            onPreviousStep={onBackToEmailStep}
            userInfo={onboardingInfo}
            setUserInfo={setOnboardingInfo}
          />
        )}
        {currentStep === ONBOARDING_STEPS.ABOUT_PROJECT && (
          <AboutProject
            onNextStep={onSignUpGetStarted}
            onPreviousStep={onBackToUserInfoStep}
            onboardingInfo={onboardingInfo}
            setOnboardingInfo={setOnboardingInfo}
            isAuthorizedDistributor={isAuthorizedDistributor}
            hasInviteToken={!!inviteToken}
          />
        )}
      </OnboardingTemplate>
    </OnboardingContext.Provider>
  );
};
